import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from '../lib/axios';
import { 
  Calendar, MapPin, Clock, Users, List, Map, CalendarDays, 
  Filter, ChevronDown, Search, Plus, Ticket, Heart, Check,
  ChevronLeft, ChevronRight, ArrowRight, X, SlidersHorizontal
} from 'lucide-react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth, useIsland, useCurrency, API } from '../App';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon
const createCustomIcon = (color = '#3b82f6') => {
  return L.divIcon({
    className: 'custom-event-marker',
    html: `<div style="background-color: ${color}; width: 32px; height: 32px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"><div style="transform: rotate(45deg); width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 14px;">📅</div></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// Island center coordinates
const ISLAND_CENTERS = {
  curacao: { lat: 12.1696, lng: -68.9900, zoom: 11 },
  aruba: { lat: 12.5211, lng: -69.9683, zoom: 12 },
  bonaire: { lat: 12.2019, lng: -68.2624, zoom: 12 },
  all: { lat: 12.3, lng: -69.0, zoom: 9 }
};

const EventsPage = () => {
  const { user, token } = useAuth();
  const { selectedIsland } = useIsland();
  const { formatPrice } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // list, map, calendar
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedMapEvent, setSelectedMapEvent] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCategories();
    fetchEvents();
  }, [selectedIsland, selectedCategory]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/events/categories`);
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedIsland && selectedIsland) {
        params.append('island', selectedIsland);
      }
      if (selectedCategory) {
        params.append('category', selectedCategory);
      }
      
      const res = await axios.get(`${API}/events?${params.toString()}`);
      
      // Separate upcoming and past events
      const now = new Date();
      const upcoming = [];
      const past = [];
      
      (res.data.events || []).forEach(event => {
        const eventDate = new Date(event.event_date);
        if (eventDate >= now) {
          upcoming.push(event);
        } else {
          past.push(event);
        }
      });
      
      setEvents(upcoming);
      setPastEvents(past.slice(0, 6)); // Limit past events shown
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    if (categoryId) {
      setSearchParams({ category: categoryId });
    } else {
      setSearchParams({});
    }
  };

  const getCategoryById = (id) => categories.find(c => c.id === id);

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter events by search query
  const filteredEvents = useMemo(() => {
    if (!searchQuery.trim()) return events;
    const query = searchQuery.toLowerCase();
    return events.filter(event => 
      event.title?.toLowerCase().includes(query) ||
      event.description?.toLowerCase().includes(query) ||
      event.location?.venue_name?.toLowerCase().includes(query)
    );
  }, [events, searchQuery]);

  // Group events by date for calendar view
  const eventsByDate = useMemo(() => {
    const grouped = {};
    events.forEach(event => {
      const dateKey = new Date(event.event_date).toDateString();
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(event);
    });
    return grouped;
  }, [events]);

  // Get events for a specific month
  const getCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    for (let i = 0; i < 42; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      days.push({
        date: currentDate,
        isCurrentMonth: currentDate.getMonth() === month,
        events: eventsByDate[currentDate.toDateString()] || []
      });
    }
    return days;
  };

  const EventCard = ({ event, isPast = false }) => {
    const category = getCategoryById(event.category);
    
    return (
      <Link 
        to={`/events/${event.slug || event.id}`}
        className={`block bg-card rounded-2xl border border-border overflow-hidden hover:shadow-lg transition-all hover:border-primary/30 ${isPast ? 'opacity-70' : ''}`}
        data-testid={`event-card-${event.id}`}
      >
        {/* Event Image */}
        <div className="aspect-[16/9] bg-secondary relative">
          {event.images?.[0] || event.image ? (
            <img src={event.images?.[0] || event.image} alt={event.title} className="w-full h-full object-cover" />
          ) : (
            <div 
              className="w-full h-full flex items-center justify-center text-6xl"
              style={{ backgroundColor: category?.color + '20' }}
            >
              {category?.icon || '📅'}
            </div>
          )}
          
          {/* Date Badge */}
          <div className="absolute top-3 left-3 bg-white dark:bg-slate-800 rounded-xl px-3 py-2 text-center shadow-lg">
            <div className="text-xs font-medium text-primary uppercase">
              {new Date(event.event_date).toLocaleDateString('en-US', { month: 'short' })}
            </div>
            <div className="text-xl font-bold text-foreground">
              {new Date(event.event_date).getDate()}
            </div>
          </div>

          {/* Price Badge - Check tickets first, then price */}
          {(() => {
            // Get minimum ticket price if tickets exist
            const minTicketPrice = event.tickets && event.tickets.length > 0
              ? Math.min(...event.tickets.map(t => t.price || 0))
              : null;
            
            // Determine display price
            const displayPrice = minTicketPrice !== null ? minTicketPrice : event.price;
            const isFree = displayPrice === 0 || displayPrice === null;
            
            return isFree ? (
              <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                Free
              </div>
            ) : (
              <div className="absolute top-3 right-3 bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                {event.tickets && event.tickets.length > 1 ? 'From ' : ''}
                {formatPrice(displayPrice, event.price_currency || 'USD')}
              </div>
            );
          })()}

          {/* Live/Ended Badge */}
          <div className="absolute bottom-3 right-3">
            {event.status === 'cancelled' ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-full shadow-md">
                <Clock className="w-3 h-3" />
                Cancelled
              </span>
            ) : isPast ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-full shadow-md">
                <Clock className="w-3 h-3" />
                Ended
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-full shadow-md animate-pulse">
                <span className="w-2 h-2 bg-white rounded-full"></span>
                Live
              </span>
            )}
          </div>

          {isPast && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="text-white font-semibold">Past Event</span>
            </div>
          )}
        </div>

        {/* Event Info */}
        <div className="p-4">
          {/* Category */}
          <div className="flex items-center gap-2 mb-2">
            <span 
              className="px-2 py-0.5 rounded-full text-xs font-medium"
              style={{ 
                backgroundColor: category?.color + '20', 
                color: category?.color 
              }}
            >
              {category?.icon} {category?.name || event.category}
            </span>
          </div>

          {/* Title */}
          <h3 className="font-semibold text-foreground mb-2 line-clamp-2">{event.title}</h3>

          {/* Time & Location */}
          <div className="space-y-1 text-sm text-muted-foreground mb-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 flex-shrink-0" />
              <span>{formatTime(event.event_date)}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{event.location?.venue_name || event.location?.address}</span>
            </div>
          </div>

          {/* RSVP Stats */}
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div className="flex items-center gap-3 text-sm">
              <span className="flex items-center gap-1 text-muted-foreground">
                <Heart className="w-4 h-4" /> {event.interested_count || 0}
              </span>
              <span className="flex items-center gap-1 text-green-500">
                <Check className="w-4 h-4" /> {event.going_count || 0} going
              </span>
            </div>
            
            {!isPast && (
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        </div>
      </Link>
    );
  };

  // Sidebar Filter Component
  const SidebarFilters = ({ isMobile = false }) => (
    <div className={`space-y-6 ${isMobile ? '' : ''}`}>
      {/* Categories */}
      <div className="bg-card rounded-xl border border-border p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Filter className="w-4 h-4" />
          Categories
        </h3>
        <div className="space-y-1">
          <button
            onClick={() => { handleCategorySelect(''); if (isMobile) setShowMobileFilters(false); }}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${
              !selectedCategory 
                ? 'bg-primary text-white' 
                : 'text-foreground hover:bg-secondary'
            }`}
          >
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              All Events
            </span>
            <span className="text-xs opacity-70">{events.length + (selectedCategory ? 0 : pastEvents.length)}</span>
          </button>
          
          {categories.map(cat => {
            const count = events.filter(e => e.category === cat.id).length;
            return (
              <button
                key={cat.id}
                onClick={() => { handleCategorySelect(cat.id); if (isMobile) setShowMobileFilters(false); }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${
                  selectedCategory === cat.id
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-foreground hover:bg-secondary'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span>{cat.icon}</span>
                  {cat.name}
                </span>
                {count > 0 && <span className="text-xs opacity-70">{count}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Time Filter */}
      <div className="bg-card rounded-xl border border-border p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4" />
          Time Period
        </h3>
        <div className="space-y-1">
          <button
            onClick={() => { setShowPastEvents(false); if (isMobile) setShowMobileFilters(false); }}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
              !showPastEvents 
                ? 'bg-primary/10 text-primary font-medium' 
                : 'text-foreground hover:bg-secondary'
            }`}
          >
            Upcoming Events
          </button>
          <button
            onClick={() => { setShowPastEvents(true); if (isMobile) setShowMobileFilters(false); }}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
              showPastEvents 
                ? 'bg-primary/10 text-primary font-medium' 
                : 'text-foreground hover:bg-secondary'
            }`}
          >
            Past Events
          </button>
        </div>
      </div>

      {/* View Mode (Mobile only) */}
      {isMobile && (
        <div className="bg-card rounded-xl border border-border p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">View Mode</h3>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => { setViewMode('list'); setShowMobileFilters(false); }}
              className={`p-2 rounded-lg text-sm transition-colors flex flex-col items-center gap-1 ${
                viewMode === 'list' ? 'bg-primary text-white' : 'bg-secondary text-foreground'
              }`}
            >
              <List className="w-4 h-4" />
              List
            </button>
            <button
              onClick={() => { setViewMode('calendar'); setShowMobileFilters(false); }}
              className={`p-2 rounded-lg text-sm transition-colors flex flex-col items-center gap-1 ${
                viewMode === 'calendar' ? 'bg-primary text-white' : 'bg-secondary text-foreground'
              }`}
            >
              <CalendarDays className="w-4 h-4" />
              Calendar
            </button>
            <button
              onClick={() => { setViewMode('map'); setShowMobileFilters(false); }}
              className={`p-2 rounded-lg text-sm transition-colors flex flex-col items-center gap-1 ${
                viewMode === 'map' ? 'bg-primary text-white' : 'bg-secondary text-foreground'
              }`}
            >
              <Map className="w-4 h-4" />
              Map
            </button>
          </div>
        </div>
      )}

      {/* Create Event CTA */}
      {user && (
        <Link
          to="/events/create"
          className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Create Event
        </Link>
      )}
    </div>
  );

  // Mobile-friendly Agenda View for Calendar
  const AgendaView = () => {
    const upcomingDates = Object.keys(eventsByDate)
      .filter(dateStr => new Date(dateStr) >= new Date().setHours(0,0,0,0))
      .sort((a, b) => new Date(a) - new Date(b))
      .slice(0, 14);

    if (upcomingDates.length === 0) {
      return (
        <div className="text-center py-12">
          <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No upcoming events scheduled</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {upcomingDates.map(dateStr => {
          const dayEvents = eventsByDate[dateStr];
          const date = new Date(dateStr);
          const isToday = date.toDateString() === new Date().toDateString();
          
          return (
            <div key={dateStr} className="bg-card rounded-xl border border-border overflow-hidden">
              <div className={`px-4 py-2 ${isToday ? 'bg-primary text-white' : 'bg-secondary'}`}>
                <div className="font-medium">
                  {isToday ? 'Today' : date.toLocaleDateString('en-US', { weekday: 'long' })}
                </div>
                <div className="text-sm opacity-80">
                  {date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                </div>
              </div>
              <div className="divide-y divide-border">
                {dayEvents.map(event => {
                  const category = getCategoryById(event.category);
                  return (
                    <Link
                      key={event.id}
                      to={`/events/${event.slug || event.id}`}
                      className="flex items-center gap-3 p-3 hover:bg-secondary/50 transition-colors"
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-lg flex-shrink-0"
                        style={{ backgroundColor: category?.color + '20' }}
                      >
                        {category?.icon || '📅'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-foreground truncate">{event.title}</div>
                        <div className="text-sm text-muted-foreground flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          {formatTime(event.event_date)}
                          {event.location?.venue_name && (
                            <>
                              <span>•</span>
                              <span className="truncate">{event.location.venue_name}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2" data-testid="events-page-title">
              <Calendar className="w-7 h-7 text-primary" />
              Events
            </h1>
            <p className="text-muted-foreground mt-1">
              Discover what's happening {selectedIsland && selectedIsland ? `in ${selectedIsland.charAt(0).toUpperCase() + selectedIsland.slice(1)}` : 'on the islands'}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Mobile Filter Button */}
            <button
              onClick={() => setShowMobileFilters(true)}
              className="lg:hidden flex items-center gap-2 px-4 py-2 bg-secondary text-foreground rounded-xl"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
            </button>

            {/* View Toggle - Desktop */}
            <div className="hidden sm:flex bg-secondary rounded-xl p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                  viewMode === 'list' ? 'bg-card text-foreground shadow' : 'text-muted-foreground'
                }`}
              >
                <List className="w-4 h-4" />
                List
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                  viewMode === 'calendar' ? 'bg-card text-foreground shadow' : 'text-muted-foreground'
                }`}
              >
                <CalendarDays className="w-4 h-4" />
                Calendar
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                  viewMode === 'map' ? 'bg-card text-foreground shadow' : 'text-muted-foreground'
                }`}
              >
                <Map className="w-4 h-4" />
                Map
              </button>
            </div>

            {user && (
              <Link
                to="/events/create"
                className="hidden md:flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
                data-testid="create-event-btn"
              >
                <Plus className="w-5 h-5" />
                Create Event
              </Link>
            )}
          </div>
        </div>

        {/* Main Content with Sidebar */}
        <div className="flex gap-6">
          {/* Left Sidebar - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <SidebarFilters />
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-card border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Active Filters Pills */}
            {(selectedCategory || showPastEvents) && (
              <div className="flex flex-wrap gap-2 mb-4">
                {selectedCategory && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                    {getCategoryById(selectedCategory)?.icon} {getCategoryById(selectedCategory)?.name}
                    <button onClick={() => handleCategorySelect('')} className="ml-1 hover:bg-primary/20 rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {showPastEvents && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-secondary text-foreground rounded-full text-sm">
                    Past Events
                    <button onClick={() => setShowPastEvents(false)} className="ml-1 hover:bg-muted rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
            )}

            {/* Content */}
            {loading ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-card rounded-2xl border border-border overflow-hidden animate-pulse">
                    <div className="aspect-[16/9] bg-secondary" />
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-secondary rounded w-1/4" />
                      <div className="h-5 bg-secondary rounded w-3/4" />
                      <div className="h-4 bg-secondary rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : viewMode === 'list' ? (
              <>
                {/* Upcoming Events */}
                {(showPastEvents ? pastEvents : filteredEvents).length > 0 ? (
                  <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
                    {(showPastEvents ? pastEvents : filteredEvents).map(event => (
                      <EventCard key={event.id} event={event} isPast={showPastEvents} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      {showPastEvents ? 'No past events' : 'No upcoming events'}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {selectedCategory 
                        ? 'No events in this category. Try a different filter.'
                        : searchQuery
                          ? 'No events match your search.'
                          : 'Be the first to create an event!'}
                    </p>
                    {user && !showPastEvents && (
                      <Link
                        to="/events/create"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl"
                      >
                        <Plus className="w-5 h-5" />
                        Create Event
                      </Link>
                    )}
                  </div>
                )}
              </>
            ) : viewMode === 'calendar' ? (
              <AgendaView />
            ) : (
              /* Map View */
              <div className="h-[600px] rounded-2xl overflow-hidden border border-border">
                <MapContainer
                  center={[
                    ISLAND_CENTERS[selectedIsland || 'curacao'].lat,
                    ISLAND_CENTERS[selectedIsland || 'curacao'].lng
                  ]}
                  zoom={ISLAND_CENTERS[selectedIsland || 'curacao'].zoom}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; OpenStreetMap contributors'
                  />
                  {events.filter(e => e.location?.latitude && e.location?.longitude).map(event => {
                    const category = getCategoryById(event.category);
                    return (
                      <Marker
                        key={event.id}
                        position={[event.location.latitude, event.location.longitude]}
                        icon={createCustomIcon(category?.color || '#3b82f6')}
                        eventHandlers={{
                          click: () => setSelectedMapEvent(event)
                        }}
                      >
                        <Popup>
                          <div className="min-w-[200px]">
                            <h3 className="font-semibold mb-1">{event.title}</h3>
                            <p className="text-sm text-gray-600 mb-2">
                              {formatDate(event.event_date)} at {formatTime(event.event_date)}
                            </p>
                            <Link
                              to={`/events/${event.slug || event.id}`}
                              className="text-sm text-primary hover:underline"
                            >
                              View Details →
                            </Link>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MapContainer>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Mobile Filters Modal */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-[150] lg:hidden">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowMobileFilters(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-background rounded-t-3xl max-h-[80vh] overflow-y-auto animate-in slide-in-from-bottom">
            <div className="sticky top-0 bg-background border-b border-border px-4 py-3 flex items-center justify-between">
              <h2 className="font-semibold text-foreground">Filters</h2>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="p-2 hover:bg-secondary rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <SidebarFilters isMobile />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default EventsPage;
